// Catalog.ID Client — Recovery Kit Assembly & Reconstruction
// ============================================================================
// Ties the pieces together per Recovery Kit Addendum:
//   create: supersecret → SSS shares (§4.1) → sheet payloads (§4.2) + fresh
//           encrypted key bag bundle (§5) the sheets reference.
//   recover: quorum of sheet payloads → supersecret display form, verified via
//           the per-line checksums (§4.1 reconstruction).

import { sssEvaluate, sssCombine, type SssShare } from './sss';
import { parseSupersecret, assembleSupersecret, BASE57_ALPHABET } from './supersecret';
import { encodeUtf8, decodeUtf8, fromBase64 } from './aes';
import {
    encodeRecoveryCard,
    type RecoveryCardPayload,
} from './recovery-card';
import {
    exportKeyBagBundle, accountFingerprint, type KeyBagBundle, type BundleBlob,
} from './bundle';
import { mintRecoveryKitEntry, type RecoveryKitEntryData } from './recovery-kit-entry';
import type { KeyBag } from './keybag';

export interface RecoveryKitProfile {
    thresholdK: number;
    totalSharesN: number;
    /** Default sheet role names, index-aligned with share indexes 1..n. */
    defaultLabels: string[];
}

/** §7 default for personal identities. */
export const PROFILE_2_OF_3: RecoveryKitProfile = {
    thresholdK: 2,
    totalSharesN: 3,
    defaultLabels: ['Personal Circle', 'Institutional', 'Owner Reserve'],
};

/** Mid-size organizations — one management seat short of §8's five. */
export const PROFILE_3_OF_4: RecoveryKitProfile = {
    thresholdK: 3,
    totalSharesN: 4,
    defaultLabels: ['Owner', 'Management 1', 'Management 2', 'Notary or lawyer'],
};

/** §8 recommendation for organization identities. */
export const PROFILE_3_OF_5: RecoveryKitProfile = {
    thresholdK: 3,
    totalSharesN: 5,
    defaultLabels: [
        'Owner Delegate 1', 'Owner Delegate 2', 'Notary / Registered Agent',
        'Outside Custodian', 'Organization Archive',
    ],
};

/** Upper bound on ADDITIONAL printed copies of ONE sheet type. The master
 *  document set always holds each sheet exactly once — extras are printed after
 *  it. Copies buy availability and cost exposure (§9 amendment), so the count
 *  stays small and hand-countable. */
export const MAX_CARD_EXTRA_COPIES = 5;

export interface CreateRecoveryKitParams {
    cid: string;
    supersecret: string;
    thresholdK: number;
    totalSharesN: number;
    /** Sheet labels; missing entries fall back to profile defaults or empty. */
    cardLabels?: string[];
    /** How many ADDITIONAL copies of each sheet TYPE to print beyond the one in
     *  the master set (0..{@link MAX_CARD_EXTRA_COPIES}, default 0, one entry
     *  per share). Copies are a PRINT concept: every copy of a type carries the
     *  IDENTICAL share and counts as one sheet toward the threshold (§2.3) —
     *  they buy availability, never a lower quorum. */
    cardExtraCopies?: number[];
    /** Decrypted key bag — re-encrypted into the kit's bundle under a fresh salt. */
    bag: KeyBag;
    publicKeys: { keyId: string; algorithm: string; publicKey: string; keyIndex?: number; status?: string }[];
    /** The account's blobs, carried into the kit's bundle as-is
     *  (cid-encrypted-storage §4.2). `[]` for an account that has none. */
    blobs: BundleBlob[];
    now?: Date;
}

export interface RecoveryKit {
    recoverySetId: string;
    thresholdK: number;
    totalSharesN: number;
    createdAt: number;
    verificationKey: Uint8Array;
    /** The bundle this kit was created against — sheets carry its fingerprint. */
    bundle: KeyBagBundle;
    cards: { payload: RecoveryCardPayload; encoded: Uint8Array; extraCopies: number }[];
    accountFingerprint: string;
}

export interface BuildRecoveryKitParams {
    /** The stored set — coefficients, set id, verification key, labels, k/n.
     *  Every print of a set comes from here and NEVER from fresh randomness
     *  (§16.6), or the sheets would not combine with the ones already on paper. */
    entry: RecoveryKitEntryData;
    cid: string;
    /** The supersecret whose 44 random characters are the polynomials' constant
     *  terms. It is verified against its own line checksums first. */
    supersecret: string;
    /** Decrypted key bag — re-encrypted into THIS print's bundle under a fresh salt. */
    bag: KeyBag;
    publicKeys: { keyId: string; algorithm: string; publicKey: string; keyIndex?: number; status?: string }[];
    /** The account's blobs, carried into the bundle as-is (cid-encrypted-storage
     *  §4.2). Still encrypted under derive-root-derived keys, which the bundle's
     *  own `derive_root` entry re-derives. `[]` for an account with none, and
     *  for an ORGANIZATION, which has no blobs (§5.1). */
    blobs: BundleBlob[];
    /** Which sheet types to evaluate, 1..n. Default: the whole set. A reprint
     *  asks for the subset the member is handing out. */
    indexes?: number[];
    /** ADDITIONAL printed copies per sheet TYPE — one entry per share index
     *  1..n, whether or not that index is being printed this time. */
    cardExtraCopies?: number[];
    /** The PRINT time. Stamps the bundle; the sheets' `created_at` comes from
     *  the entry, which is the SET's creation (§16.4). */
    now?: Date;
}

/**
 * Build a printable kit from a stored entry: a fresh key-bag bundle plus the
 * requested sheets, each carrying the entry's set id, verification key,
 * creation time and label, and THIS export's bundle fingerprint.
 *
 * Called at creation (through {@link createRecoveryKit}) and at every later
 * reprint. A reprinted sheet is share-identical and set-identical to the
 * original — it combines with any other sheet of the set — but not
 * byte-identical: the bundle fingerprint, and with it the MAC and the printed
 * 16-character checksum, belong to the print (§16.4). Nothing in the codebase
 * compares those across sheets.
 */
export async function buildRecoveryKit(params: BuildRecoveryKitParams): Promise<RecoveryKit> {
    const { entry } = params;
    const now = params.now ?? new Date();
    const n = entry.totalSharesN;

    const indexes = params.indexes ?? Array.from({ length: n }, (_, i) => i + 1);
    if (indexes.length === 0) throw new Error('Choose at least one sheet to print');
    const seen = new Set<number>();
    for (const index of indexes) {
        if (!Number.isInteger(index) || index < 1 || index > n) {
            throw new Error(`Sheet ${index} is not part of this recovery set (it has ${n} sheets)`);
        }
        if (seen.has(index)) {
            throw new Error(`Sheet ${String.fromCharCode(64 + index)} was requested twice — `
                + 'ask for additional COPIES of a sheet instead');
        }
        seen.add(index);
    }

    const extraCopies = params.cardExtraCopies;
    if (extraCopies) {
        if (extraCopies.length !== n) {
            throw new Error(`cardExtraCopies must have one entry per sheet (${n}), `
                + `got ${extraCopies.length}`);
        }
        for (const c of extraCopies) {
            if (!Number.isInteger(c) || c < 0 || c > MAX_CARD_EXTRA_COPIES) {
                throw new Error(`Each sheet may have between 0 and ${MAX_CARD_EXTRA_COPIES} additional copies`);
            }
        }
    }

    const { random44, lineErrors } = parseSupersecret(params.supersecret);
    if (lineErrors.length > 0) {
        throw new Error('Supersecret failed checksum validation — cannot create a Recovery Kit from it');
    }

    const bundle = await exportKeyBagBundle({
        cid: params.cid,
        bag: params.bag,
        supersecret: params.supersecret,
        publicKeys: params.publicKeys,
        createdAt: now.toISOString(),
        blobs: params.blobs,
    });

    const verificationKey = fromBase64(entry.verificationKey);
    const acctFp = await accountFingerprint(params.cid);

    const secret = encodeUtf8(random44);
    const coefficients = fromBase64(entry.coefficients);
    const shares = sssEvaluate(secret, coefficients, indexes);
    secret.fill(0);
    coefficients.fill(0);

    const cards = shares.map((share: SssShare) => {
        const payload: RecoveryCardPayload = {
            version: 1,
            recoverySetId: entry.setId,
            thresholdK: entry.thresholdK,
            totalSharesN: n,
            shareIndex: share.index,
            shareY: share.data,
            bundleFingerprint: bundle.bundle_fingerprint,
            accountFingerprint: acctFp,
            createdAt: entry.createdAt,
            cardLabel: entry.labels[share.index - 1] ?? '',
            verificationKey,
        };
        return {
            payload,
            encoded: encodeRecoveryCard(payload),
            extraCopies: extraCopies?.[share.index - 1] ?? 0,
        };
    });

    return {
        recoverySetId: entry.setId,
        thresholdK: entry.thresholdK,
        totalSharesN: n,
        createdAt: entry.createdAt,
        verificationKey,
        bundle,
        cards,
        accountFingerprint: acctFp,
    };
}

/**
 * Mint a NEW set and print all of it — the composition of
 * {@link mintRecoveryKitEntry} and {@link buildRecoveryKit}, kept for the
 * callers and tests that pin today's kit shape.
 *
 * Round 9 note: a caller that wants the set to be REPRINTABLE must mint the
 * entry itself and persist it in the key bag (§16.6); a kit created through
 * this function alone exists for exactly one print, as every kit did before.
 */
export async function createRecoveryKit(params: CreateRecoveryKitParams): Promise<RecoveryKit> {
    const now = params.now ?? new Date();
    const entry = mintRecoveryKitEntry({
        thresholdK: params.thresholdK,
        totalSharesN: params.totalSharesN,
        labels: params.cardLabels ?? [],
        now,
    });
    return buildRecoveryKit({
        entry,
        cid: params.cid,
        supersecret: params.supersecret,
        bag: params.bag,
        publicKeys: params.publicKeys,
        blobs: params.blobs,
        cardExtraCopies: params.cardExtraCopies,
        now,
    });
}

/**
 * Reconstruct the supersecret display form from a quorum of decoded sheet
 * payloads (§4.1 / §15 path B). Validates set consistency, then checks that
 * every reconstructed byte is a base57 alphabet character: a below-threshold
 * combination yields uniformly wrong bytes, so the alphabet check catches it
 * with false-pass probability (57/256)⁴⁴ ≈ 10⁻²⁹. (The per-line checksums
 * cannot serve here — they are recomputed FROM the reconstructed data.)
 */
export function reconstructSupersecret(cards: RecoveryCardPayload[]): string {
    if (cards.length === 0) throw new Error('No Recovery Sheets provided');
    const first = cards[0];
    const seen = new Set<number>();
    for (const c of cards) {
        if (c.recoverySetId !== first.recoverySetId) {
            throw new Error('Sheets are from different recovery sets — collect sheets from ONE set');
        }
        if (c.thresholdK !== first.thresholdK || c.totalSharesN !== first.totalSharesN) {
            throw new Error('Sheets disagree on the threshold profile — one of them is damaged');
        }
        if (seen.has(c.shareIndex)) {
            throw new Error(`Two copies of sheet type ${String.fromCharCode(64 + c.shareIndex)} were scanned — `
                + 'copies of the same type do not count toward the threshold; scan a DIFFERENT sheet type');
        }
        seen.add(c.shareIndex);
    }
    if (cards.length < first.thresholdK) {
        throw new Error(`This recovery set needs ${first.thresholdK} distinct sheet types; only ${cards.length} scanned`);
    }

    const combined = sssCombine(cards.map(c => ({ index: c.shareIndex, data: c.shareY })));
    const valid = combined.every(b => BASE57_BYTES.has(b));
    if (!valid) {
        combined.fill(0);
        throw new Error('Reconstruction failed — the scanned sheets do not form a valid quorum of this set');
    }
    const random44 = decodeUtf8(combined);
    combined.fill(0);
    return assembleSupersecret(random44);
}

const BASE57_BYTES = new Set(Array.from(BASE57_ALPHABET, ch => ch.charCodeAt(0)));
